import { Component, Input, ViewChild, Injector, OnChanges, SimpleChanges } from '@angular/core';

import { Subscription, Observable } from 'rxjs';
import { filter, map, mergeMap } from 'rxjs/operators';
import { MessageService } from 'primeng/api';


import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';


import { TabComponent } from '../../../Common/Layout/components/Tab/tab.component';
import { userPermissionsEnum } from '../../../Core/Users/enums/user-permissions.enum';
import { ButtonTypesEnum } from '../../../Common/Forms/enums/button-types.enum';
import { attributeTypesEnum } from '../../../Common/Misc/enums/attribute-types.enum';
import { ViSourceOutlet } from '../../models/vi-source-outlet.model';
import { FilterService } from '../../../Common/Filter/services/abstract-filter.service';
import { ViSourceOutletSearchService } from '../../services/vi-source-outlet-search.service';
import { FilterGroupModel } from '../../../Common/Filter/models/filter-group.model';
import { SortFieldEnum } from '../../../Common/TargetingShared/enum/SortField.enum';
import { CirculationType } from '../../../Discover/Targeting/models/circulation.model';
import { SortOrderEnum } from '../../../api/shared/enums/sort-order.enum';
import { LocalizedEnum } from '../../../api/journodex/model/localized-enum';
import { messageSeverityEnum } from '../../../Common/Misc/enums/message-severity.enum';
import { TargetingAddToTypeEnum } from '../../../Discover/Targeting/enums/targeting-add-to-type.enum';
import { TargetingListList } from '../../../Discover/Targeting/models/targeting-list-list.model';
import { ListPickerService } from '../../../Common/RhpContexts/services/list-picker.service';
import { CoreDialogService } from '../../../Core/Dialogs/services/dialog.service';
import { DistPickerDialogComponent } from '../../../Common/RhpContexts/components/DistPickerDialog/dist-picker-dialog.component';
import { ChipModel } from '../../../Common/Forms/models/chip.model';
import { DistributionSummaryModel } from '../../../Common/TargetingShared/model/distribution-summary-model';
import { DistributionPickerService } from '../../../Common/RhpContexts/services/distribution-picker.service';
import { ChipContainerComponent } from '../../../Common/Forms/components/ChipContainer/chip-container.component';
import { TargetingListService } from '../../../Discover/Targeting/services/targeting-list.service';
import { DistributionService } from '../../../Common/TargetingShared/services/distribution.service';
import { DistributionStatus } from '../../../Common/TargetingShared/enum/distribution-status.enum';
import { targetingAddToOptions } from '../../../Discover/Targeting/models/targeting-add-to-options.model';
import { AppearCondition, AutocompleteCustomOption } from '../../../Common/Forms/components/Autocomplete/autocomplete-custom-option.model';
import { CommonApiErrorEnum } from '../../../api/genericApi/enums/common-api-error.enum';
import { ListPickerDialogComponent } from '../../../Common/RhpContexts/components/ListPickerDialog/list-picker-dialog.component';
import { TargetingRhpDialogService } from '../../../Common/TargetingShared/services/targeting-rhp-dialog.service';
import {
    ManageListDialogComponent
} from '../../../Discover/Targeting/components/TargetingResults/ManageListDialog/manage-list-dialog.component';
import { TargetingResultModel } from '../../../Discover/Targeting/models/targeting-result.model';
import { TargetingList } from '../../../Discover/Targeting/models/targeting-list.model';
import { EntityType } from '../../../api/targeting/enum/entity.type.enum';
import { OutletModel } from '../../../Discover/Targeting/models/outlet.model';

@Component({
    selector: 'agpr-vi-source-outlets-tab',
    templateUrl: './vi-source-outlets-tab.component.html',
    styleUrls: ['./vi-source-outlets-tab.component.scss'],
    providers: [
        ViSourceOutletSearchService,
        {
            provide: TabComponent,
            useExisting: ViSourceOutletsTabComponent
        },
        {
            provide: 'SortOrderServiceInterface',
            useExisting: ViSourceOutletSearchService
        },
        {
            provide: 'PaginatorServiceInterface',
            useExisting: ViSourceOutletSearchService
        },
        {
            provide: FilterService,
            useExisting: ViSourceOutletSearchService
        }
    ],
    standalone: false
})
export class ViSourceOutletsTabComponent extends TabComponent implements OnChanges {

    @Input() id: string = 'aiSourceOutletsTab';
    @Input() icon: string = 'fa-city';
    @Input() heading: string = 'Vi.qnA.tab.sourceOutlet';
    @Input() tooltip: string = 'Vi.qnA.tab.sourceOutlet';
    @Input() outletList: ViSourceOutlet[] = [];
    @Input() loading: boolean = false;

    @ViewChild('listChipsComponent') listChipsComponent: ChipContainerComponent;

    addToType: TargetingAddToTypeEnum = TargetingAddToTypeEnum.LIST;
    filteredOutletList: ViSourceOutlet[] = [];
    areChipsVisible: boolean = false;
    hasTargetingPermission: boolean = false;
    selectedLists: TargetingListList[] = [];
    selectedListsChips: ChipModel[] = [];
    selectedOutlets: ViSourceOutlet[] = [];
    allSelected: boolean = false;
    distributions$: Observable<DistributionSummaryModel[]>;
    selectedDistributions: DistributionSummaryModel[] = [];
    selectedDistributionsChips: ChipModel[] = [];
    freshCreatedId: number;
    totalNumberOfPages: number;

    targetingLists$: Observable<TargetingListList[]>;

    private sortOrder: SortOrderEnum;
    private sortField: LocalizedEnum;
    private targetingLists: TargetingListList[];
    private subscriptions: Subscription[] = [];

    readonly buttonType = ButtonTypesEnum;
    readonly attributeType = attributeTypesEnum;
    readonly messageSeverity = messageSeverityEnum;
    readonly targetingAddToType = TargetingAddToTypeEnum;
    readonly targetingAddToOptions = targetingAddToOptions;
    readonly listBuilderOptions: AutocompleteCustomOption[] = [{
        label: 'Discover.listBuilder.createList',
        action: (listName: string) => {
            if (listName.length === 0) {
                return;
            }
            this.createList(listName);
        },
        appearCondition: AppearCondition.noExactMatchInSuggestions,
        clearOnSelect: false
    }];

    constructor(
        private currentUserStateService: CurrentUserStateService,
        private viSourceOutletSearchService: ViSourceOutletSearchService,
        private coreDialogService: CoreDialogService,
        private targetingListService: TargetingListService,
        private distributionService: DistributionService,
        private targetingRhpDialogService: TargetingRhpDialogService,
        private messageService: MessageService,
        private injector: Injector,
        public listPickerService: ListPickerService,
        public distributionPickerService: DistributionPickerService,
    ) {
        super();

        this.targetingLists$ = this.targetingListService.all$;
        this.distributions$ = this.distributionService.getDistributionSummaries().pipe(
            filter(x => !!x),
            map(dist => dist.filter(element => element.status === DistributionStatus.draft))
        );

        this.subscriptions.push(
            this.targetingListService.all$.subscribe((lists) => {
                this.targetingLists = lists;
            }),
            this.currentUserStateService.hasAnyPermissions([userPermissionsEnum.advancedTargeting]).subscribe(
                (hasPerm) => {
                    this.hasTargetingPermission = hasPerm;
                }
            ),
            this.viSourceOutletSearchService.getFilters().subscribe(filters => {
                this.areChipsVisible = !!filters.find(chipFilter => {
                    if (chipFilter instanceof FilterGroupModel) {
                        return chipFilter.children.find(x => x.chips && x.chips.length > 0);
                    } else {
                        return chipFilter.chips && chipFilter.chips.length > 0;
                    }
                });
            }),
            this.viSourceOutletSearchService.getDisplayResults().subscribe(displayResults => {
                this.selectNone();
                this.filteredOutletList = displayResults;
                this.setDisplayedReachValue();
            }),
            this.viSourceOutletSearchService.getSortOrder().subscribe(x => {
                this.sortOrder = x;
                this.setDisplayedReachValue();
            }),
            this.viSourceOutletSearchService.getSortField().subscribe(x => {
                this.sortField = x;
                this.setDisplayedReachValue();
            }),
            this.viSourceOutletSearchService.getMaxPageCriteria().subscribe(n => {
                this.totalNumberOfPages = n;
            }),
            this.listPickerService.getWorkingLists().pipe(
                filter(x => !!x)
            ).subscribe(lists => {
                this.selectedLists = lists;
                this.selectedListsChips = lists.map(list => ({
                    label: list.name
                }));
            }),
            this.distributionPickerService.getWorkingDistributions().pipe(
                filter(x => !!x)
            ).subscribe(distributions => {
                this.selectedDistributions = distributions;
                this.selectedDistributionsChips = distributions.map(list => ({
                    label: list.name
                }));
            }),
        );
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['outletList']) {
            this.selectNone();
            this.viSourceOutletSearchService.setResults(this.outletList ?? []);
        }
    }

    selectTargetingType() {
        this.listPickerService.clearWorkingLists();
        this.distributionPickerService.clearWorkingDistributions();
    }

    addTo() {
        if (this.addToType === TargetingAddToTypeEnum.LIST) {
            this.addToList();
        } else {
            this.addToDistribution();
        }
    }

    newlyAddedList() {
        if (this.freshCreatedId) {
            const freshCreatedList = this.targetingLists.find(x => x.id === this.freshCreatedId);
            this.listPickerService.addWorkingList(freshCreatedList);
            this.freshCreatedId = undefined;
        }
    }

    updateListBuilder() {
        this.listPickerService.setWorkingLists(this.selectedLists);
        this.selectedListsChips = this.selectedLists.map(workingList => ({
            label: workingList.name
        }));
    }

    toggleSelectAll() {
        if (this.filteredOutletList.length <= this.selectedOutlets.length) {
            this.selectNone();
        } else {
            this.selectedOutlets = this.filteredOutletList.slice();
        }
    }

    updateDistributionBuilder() {
        this.distributionPickerService.setWorkingDistributions(this.selectedDistributions);
        this.selectedDistributionsChips = this.selectedDistributions.map(workingDistribution => ({
            label: workingDistribution.name
        }));
        this.distributionPickerService.setWorkingDistributionsLock(true);
    }

    selectNone(): void {
        this.listPickerService.setAllResultsSelected(false);
        this.distributionPickerService.setAllResultsSelected(false);
        this.allSelected = false;
        this.selectedOutlets = [];
    }

    selectAllResults() {
        this.listPickerService.setAllResultsSelected(true, this.outletList.length);
        this.distributionPickerService.setAllResultsSelected(true, this.outletList.length);
        this.allSelected = true;
        this.selectedOutlets = this.outletList.slice();
    }

    selectedOutletsChanged() {
        this.allSelected = this.selectedOutlets.length === this.outletList.length;
    }

    clearListChips() {
        this.listChipsComponent.chipPopover.hide();
        this.listPickerService.clearWorkingLists();
    }


    clearDistributionChips() {
        this.listChipsComponent.chipPopover.hide();
        this.distributionPickerService.clearWorkingDistributions();
    }

    expandOutletProfile(outletId: string) {
        const io = {
            inputs: {
                outletId: outletId,
                entityType: EntityType.OUTLET,
                fullHeight: true,
                disableContentToggle: true
            },
            outputs: {
                dialogClose: () => {}
            }
        };

        this.targetingRhpDialogService.openTakeOver(io);
    }

    openManageListDialog(entity: OutletModel) {
        const io = {
            inputs: {
                setEntity: entity
            },
            outputs: {}
        };

        this.coreDialogService.open(ManageListDialogComponent, io);
    }

    openUpsell() {
        window.open('https://www.agilitypr.com/contact/');
    }

    private createList(listName: string) {
        this.targetingListService.isNameDuplicated$(listName).pipe(
            mergeMap((duplicate) => {
                if (duplicate) {
                    throw new Error(CommonApiErrorEnum.duplicateName);
                } else {
                    const list = new TargetingList({ name: listName });
                    return this.targetingListService.create(list);
                }
            })
        ).subscribe({
            next: (id) => {
                this.freshCreatedId = id;
                this.newlyAddedList();
            },
            error: () => {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: 'Discover.dialogs.newList.duplicate',
                });
            }
        });
    }

    private addToList() {
        const targetingResults = this.selectedOutlets.map(x => {
            const targetingResult = new TargetingResultModel();
            targetingResult.id = x.outlet.id;
            targetingResult.entityType = EntityType.OUTLET;
            targetingResult.outletModel = x.outlet;
            return targetingResult;
        });
        this.listPickerService.setSelectedEntities(targetingResults);
        if (this.selectedListsChips?.length > 0) {
            this.listPickerService.addSelectedEntitiesToWorkingLists();
        } else {
            this.coreDialogService.open(ListPickerDialogComponent, {
                inputs: {
                    addMode: true
                },
                outputs: {}
            }, this.injector);
        }
    }

    private addToDistribution() {
        const targetingResults = this.selectedOutlets.map(x => {
            const targetingResult = new TargetingResultModel();
            targetingResult.id = x.outlet.id;
            targetingResult.entityType = EntityType.OUTLET;
            targetingResult.outletModel = x.outlet;
            return targetingResult;
        });
        this.distributionPickerService.setSelectedEntities(targetingResults);
        if (this.selectedListsChips?.length > 0) {
            this.distributionPickerService.addSelectedEntitiesToWorkingDists();
        } else {
            this.openDistPicker();
        }
    }

    private openDistPicker(addMode: boolean = true) {
        const io = {
            inputs: {
                addMode: addMode
            },
            outputs: {}
        };

        this.coreDialogService.open(DistPickerDialogComponent, io, this.injector);
    }

    private setDisplayedReachValue() {
        if (!this.sortField || !this.sortOrder || !this.filteredOutletList.length) {
            return;
        }
        this.filteredOutletList.forEach(outletInfo => {
            outletInfo.reachValue = null;
            const circulations = outletInfo.outlet.circulations;
            outletInfo.isReachValueFromSimilarWeb = false;

            if (circulations) {
                switch (this.sortField.enumValue) {
                    case SortFieldEnum.CIRCULATION_CIRCULATION:
                        outletInfo.reachUnit = 'Discover.targeting.sortFields.circulation';
                        outletInfo.reachValue = circulations.find(x => x.circulationType.enumValue === CirculationType.circulation)?.value;
                        outletInfo.reachIcon = 'fa-solid fa-arrows-spin';
                        break;
                    case SortFieldEnum.CIRCULATION_FOLLOWERS:
                        outletInfo.reachUnit = 'Discover.targeting.sortFields.followers';
                        outletInfo.reachValue = circulations.find(x => x.circulationType.enumValue === CirculationType.followers)?.value;
                        outletInfo.reachIcon = 'fa-solid fa-user-group';
                        break;
                    case SortFieldEnum.CIRCULATION_LISTENERS:
                        outletInfo.reachUnit = 'Discover.targeting.sortFields.listeners';
                        outletInfo.reachValue = circulations.find(x => x.circulationType.enumValue === CirculationType.listeners)?.value;
                        outletInfo.reachIcon = 'fa-solid fa-head-side-headphones';
                        break;
                    case SortFieldEnum.CIRCULATION_VIEWERS:
                        outletInfo.reachUnit = 'Discover.targeting.sortFields.viewers';
                        outletInfo.reachValue = circulations.find(x => x.circulationType.enumValue === CirculationType.viewers)?.value;
                        outletInfo.reachIcon = 'fa-solid fa-eye';
                        break;
                    default:
                        outletInfo.reachUnit = 'Discover.targeting.sortFields.uniqueVisitors';
                        outletInfo.reachIcon = 'fa-solid fa-id-badge';
                        const circulation = circulations.find(x =>
                            x.circulationType.enumValue === CirculationType.manualValue || x.circulationType.enumValue === CirculationType.similarWebValue
                        );
                        if (circulation) {
                            outletInfo.reachValue = circulation.value;
                            outletInfo.isReachValueFromSimilarWeb = circulation.isSimilarWeb;
                        }
                        break;
                }
            }
        });
    }
}
